import React from 'react';
import { useNavigate } from 'react-router-dom';
import './StudentShell.css';

export type StudentNavId =
  | 'dashboard'
  | 'apply'
  | 'history'
  | 'payments'
  | 'calculator'
  | 'support'
  | 'profile';

interface StudentSidebarProps {
  currentPath: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isMobileDrawer?: boolean;
}

interface NavItem {
  id: StudentNavId;
  label: string;
  path: string;
  match: (path: string) => boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', match: (path) => path === '/dashboard' },
  { id: 'apply', label: 'Apply for a Loan', path: '/apply', match: (path) => path.startsWith('/apply') },
  { id: 'history', label: 'Loan History', path: '/dashboard/history', match: (path) => path === '/dashboard/history' },
  { id: 'payments', label: 'Payments', path: '/dashboard/payments', match: (path) => path.startsWith('/dashboard/payments') },
  { id: 'calculator', label: 'Calculator', path: '/', match: (path) => path === '/' },
  { id: 'support', label: 'Support', path: '/dashboard/support', match: (path) => path.startsWith('/dashboard/support') },
  { id: 'profile', label: 'Profile', path: '/dashboard/profile', match: (path) => path.startsWith('/dashboard/profile') },
];

const Icons: Record<StudentNavId, React.FC> = {
  dashboard: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  apply: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  ),
  history: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  payments: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  calculator: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M8 18h.01M12 18h.01M16 14v4" />
    </svg>
  ),
  support: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  profile: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

const StudentSidebar: React.FC<StudentSidebarProps> = ({
  currentPath,
  collapsed,
  onToggleCollapse,
  isMobileDrawer = false,
}) => {
  const navigate = useNavigate();
  const showLabels = !collapsed || isMobileDrawer;

  return (
    <aside
      className={`student-sidebar ${collapsed && !isMobileDrawer ? 'collapsed' : ''} ${isMobileDrawer ? 'mobile-drawer' : ''} ${isMobileDrawer && !collapsed ? 'drawer-open' : ''}`}
    >
      <div className="student-sidebar-header">
        <div className="student-sidebar-logo">
          <img src="/genlend-logo.png" alt="GenLend" />
          {showLabels && (
            <div className="student-sidebar-brand">
              <strong>GenLend</strong>
              <span>Student Loans</span>
            </div>
          )}
        </div>
        {isMobileDrawer && !collapsed && (
          <button type="button" className="student-sidebar-close" onClick={onToggleCollapse} aria-label="Close menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      <nav className="student-sidebar-nav" aria-label="Student menu">
        <ul>
          {NAV_ITEMS.map((item) => {
            const Icon = Icons[item.id];
            const active = item.match(currentPath);
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`student-nav-link ${active ? 'active' : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobileDrawer) onToggleCollapse();
                  }}
                  title={collapsed && !isMobileDrawer ? item.label : undefined}
                >
                  <span className="student-nav-icon"><Icon /></span>
                  {showLabels && <span className="student-nav-label">{item.label}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

export default StudentSidebar;
